import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '@bloom/shared';
import apiClient from '../lib/api';
import { setSentryUser, clearSentryUser } from '../lib/sentry';

// Frontend user type without password
type FrontendUser = Omit<User, 'password'>;

interface TeamMemberInfo {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface AuthState {
  user: FrontendUser | null;
  teamMember: TeamMemberInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  /**
   * Merge fields into the cached user in place.
   *
   * Use this instead of getCurrentUser() when a screen has just persisted a
   * change and only needs the cached copy to catch up. getCurrentUser() flips
   * isLoading, and ProtectedRoute renders a full-page spinner while that is
   * true — which unmounts the current page and resets every component's state,
   * including any "saved" confirmation the user was meant to read.
   */
  patchUser: (patch: Partial<User>) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, _get) => ({
      // Initial state
      user: null,
      teamMember: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (email: string, password: string): Promise<boolean> => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await apiClient.login({ email, password });
          
          if (response.success && response.data) {
            const user = response.data.user;
            const teamMember = (user as any).teamMember || null;
            set({
              user,
              teamMember,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
            // Set Sentry user context for error tracking
            setSentryUser({ id: user._id, email: teamMember?.email || user.email, role: user.role });
            return true;
          } else {
            set({
              error: response.error || 'Login failed',
              isLoading: false,
              isAuthenticated: false,
              user: null,
              teamMember: null,
            });
            return false;
          }
        } catch (error) {
          set({
            error: 'Network error occurred',
            isLoading: false,
            isAuthenticated: false,
            user: null,
            teamMember: null,
          });
          return false;
        }
      },

      logout: async (): Promise<void> => {
        try {
          set({ isLoading: true });

          await apiClient.logout();

          set({
            user: null,
            teamMember: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          // Clear Sentry user context
          clearSentryUser();
        } catch (error) {
          // Still clear local state even if server logout fails
          set({
            user: null,
            teamMember: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          // Clear Sentry user context
          clearSentryUser();
        }
      },

      getCurrentUser: async (): Promise<void> => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await apiClient.getCurrentUser();
          
          if (response.success && response.data) {
            const user = response.data;
            const teamMember = (user as any).teamMember || null;
            set({
              user,
              teamMember,
              isAuthenticated: true,
              isLoading: false,
            });
            // Set Sentry user context for error tracking
            setSentryUser({ id: user._id, email: teamMember?.email || user.email, role: user.role });
          } else {
            set({
              user: null,
              teamMember: null,
              isAuthenticated: false,
              isLoading: false,
              error: response.error || 'Failed to get user',
            });
          }
        } catch (error) {
          set({
            user: null,
            teamMember: null,
            isAuthenticated: false,
            isLoading: false,
            error: 'Network error occurred',
          });
        }
      },

      patchUser: (patch: Partial<User>): void => {
        set((state) => (state.user ? { user: { ...state.user, ...patch } } : {}));
      },

      clearError: (): void => {
        set({ error: null });
      },

      setLoading: (loading: boolean): void => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'bloom-auth',
      partialize: (state) => ({
        user: state.user,
        teamMember: state.teamMember,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Selectors for better performance
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useTeamMember = () => useAuthStore((state) => state.teamMember);